import { useMemo, useState } from "react";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import { User, Search, Gem } from "lucide-react";
import { useTranslations } from "next-intl";

import { Game, getEnabledGames } from "../data/games";
import { NotificationProvider } from "../components/notification-provider";
import { MockAuthProvider, useMockAuth } from "../contexts/MockAuthContext";
import MockAuthModal from "../components/MockAuthModal";
import StoreModal from "../components/store-modal";

const homeGames = getEnabledGames()
  .filter((game) => game.category === "Doge OS")
  .slice(0, 3);

function HomeContent() {
  const router = useRouter();
  const { isLoggedIn, openAuthModal, addRecentGame, gems } = useMockAuth();
  const tHome = useTranslations("home");
  const tApp = useTranslations("app");

  const [isStoreOpen, setIsStoreOpen] = useState(false);

  const handleGameClick = (game: Game) => {
    // If we want to strictly require login, we can keep this check.
    // But since we default to logged in, this will mostly pass.
    if (!isLoggedIn) {
      openAuthModal();
      return;
    }

    // Track recent game
    addRecentGame(game.id);

    router.push(`/games/${game.id}`);
  };

  const handleProfileClick = () => {
    router.push("/profile");
  };

  const [searchQuery, setSearchQuery] = useState("");

  const filteredGames = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return homeGames.filter((game) => {
      if (!normalizedQuery) {
        return true;
      }

      return (
        game.title.toLowerCase().includes(normalizedQuery) ||
        game.category.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [searchQuery]);

  return (
    <>
      <Head>
        <title>{tApp("title")}</title>
        <meta name="description" content={tApp("description")} />
      </Head>

      <main className="min-h-screen">
        <div className="mobile-container bg-black rounded-md shadow-xl my-1">
          <header className="flex items-center justify-between py-4 px-4">
            <div className="flex items-center">
              <div className="w-[40px] h-8 relative bg-transparent">
                <Image
                  src="/images/logo-white.png"
                  alt="Logo"
                  width={40}
                  height={32}
                  className="object-contain"
                  priority
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Gem Store Button */}
              <button
                onClick={() => setIsStoreOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] text-game-text hover:bg-[#2a3b50] transition-colors"
              >
                <span className="font-bold text-sm text-white">{gems}</span>
                <Gem className="w-4 h-4 text-purple-500 fill-purple-500" />
                {/* <Plus className="w-3 h-3 text-white" /> */}
              </button>

              {/* Profile Icon */}
              <button
                onClick={handleProfileClick}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-game-card hover:bg-[#2a3b50] transition-colors text-game-text"
                aria-label="Profile"
              >
                <User className="w-5 h-5" />
              </button>
            </div>
          </header>

          <div className="px-4 pb-4">
            {/* Search Bar */}
            <div className="relative mb-8">
              <input
                type="text"
                placeholder={tHome("searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#1a1a1a] text-white pl-4 pr-12 py-3.5 rounded-2xl border border-[#2a2a2a] focus:border-cyan-500 focus:outline-none transition-colors placeholder-gray-500"
              />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-xl bg-[#2a3b50] text-cyan-400">
                <Search className="w-5 h-5" />
              </button>
            </div>

            <section>
              <h2 className="text-game-text text-lg font-semibold mb-4">
                {tHome("games")}
              </h2>
              <div className="grid grid-cols-2 gap-2">
                {filteredGames.map((game, index) => (
                  <GameCard
                    key={game.id}
                    game={game}
                    index={index}
                    onClick={() => handleGameClick(game)}
                  />
                ))}
                {filteredGames.length === 0 && (
                  <div className="col-span-2 text-center py-10 text-gray-500">
                    No games found
                  </div>
                )}
              </div>
            </section>
          </div>
        </div>
      </main>
      {/* Store Modal */}
      <StoreModal isOpen={isStoreOpen} onClose={() => setIsStoreOpen(false)} />
      {/* Mock Auth Modal - still here if needed but unlikely to trigger */}
      <MockAuthModal />
    </>
  );
}

interface GameCardProps {
  game: Game;
  index: number;
  onClick: () => void;
}

function GameCard({ game, index, onClick }: GameCardProps) {
  const tCats = useTranslations("categories");
  // Helper to safely get translation or fallback to original category
  // This handles cases where translation might be missing for new categories
  const categoryName = tCats.has(game.category)
    ? tCats(game.category)
    : game.category;

  return (
    <div onClick={onClick} className="game-card">
      <div className="game-image">
        <Image
          src={game.image}
          alt={game.title}
          fill
          className="object-cover select-none"
          sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
          draggable={false}
          loading={index < 4 ? "eager" : "lazy"}
        />
      </div>

      <div className="game-info">
        <div className="title-row">
          <h3 className="game-title">{game.title}</h3>
          {game.isNew && <div className="badge new">NEW</div>}
          {game.isHot && <div className="badge hot">HOT</div>}
        </div>
        <div className="divider"></div>
        <p className="game-category">{categoryName}</p>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <NotificationProvider>
      <MockAuthProvider>
        <HomeContent />
      </MockAuthProvider>
    </NotificationProvider>
  );
}
